import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function AdminToolsOverview() {
  useEffect(() => {
    document.body.classList.add('analytics-page');
    return () => {
      document.body.classList.remove('analytics-page');
    };
  }, []);

  const tools = [
    {
      name: 'Analytics Dashboard',
      description: 'Comprehensive platform analytics with revenue optimization, trends analysis, and user engagement insights',
      features: ['Revenue Optimization', 'Market Trends', 'User Engagement', 'Vendor Performance', 'Platform Overview'],
      link: '/analytics',
      icon: '📈',
      status: 'Complete'
    },
    {
      name: 'Invite Manager',
      description: 'Create and manage invite codes for beta access and user registration control',
      features: ['Generate Invite Codes', 'Track Usage', 'Set Expiration', 'Monitor Registration'],
      link: '/invite-manager',
      icon: '🎫',
      status: 'Complete'
    },
    {
      name: 'Admin Dashboard',
      description: 'Central admin control panel for platform oversight and management',
      features: ['User Management', 'Platform Controls', 'System Overview', 'Quick Actions'],
      link: '/admin-dashboard',
      icon: '⚙️',
      status: 'Complete'
    },
    {
      name: 'Staff Dashboard',
      description: 'Staff-level tools for content moderation and platform management',
      features: ['Content Moderation', 'User Support', 'Report Management', 'Staff Tools'],
      link: '/staff-dashboard',
      icon: '👥',
      status: 'Complete'
    },
    {
      name: 'Vendor Benefits Mailer',
      description: 'Email marketing tool for vendor outreach and benefits communication',
      features: ['Email Campaigns', 'Vendor Targeting', 'Benefits Promotion', 'Engagement Tracking'],
      link: '/vendorbenefitsmailer',
      icon: '📧',
      status: 'Complete'
    },
    {
      name: 'Waitlist Manager',
      description: 'View and manage email signups from the landing page waitlist',
      features: ['View All Signups', 'Export Email List', 'Signup Analytics', 'Email Management'],
      link: '/waitlist-manager',
      icon: '📋',
      status: 'Complete'
    },
    {
      name: 'Trusted Tester Program',
      description: 'ID-verified reviewer system connecting vendors with quality testers for authentic reviews',
      features: ['Tester Applications', 'Vendor Dashboard', 'Testing Workflow', 'Review Integrity'],
      link: '/trusted-tester-application',
      icon: '⭐',
      status: 'Complete'
    },
    {
      name: 'Vendor Accountability',
      description: 'Comprehensive vendor scoring and transparency tracking system',
      features: ['Transparency Scoring', 'Quality Metrics', 'Customer Service Tracking', 'Compliance Monitoring'],
      link: '/vendor-accountability',
      icon: '🎯',
      status: 'Complete'
    },
    {
      name: 'Quality Compliance Tracker',
      description: 'Monitor vendor compliance with platform quality standards',
      features: ['Lab Testing Compliance', 'Quality Standards', 'Certification Tracking', 'Improvement Plans'],
      link: '/quality-compliance',
      icon: '✅',
      status: 'Complete'
    },
    {
      name: 'Review Integrity System',
      description: 'Advanced review verification and fraud detection system',
      features: ['Review Verification', 'Fraud Detection', 'Integrity Scoring', 'Quality Alerts'],
      link: '/review-integrity',
      icon: '🛡️',
      status: 'Complete'
    }
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f7fafc', paddingTop: '60px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        <div style={{ marginBottom: '3rem', textAlign: 'center' }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#1a202c', marginBottom: '1rem' }}>
            Admin Tools & Analytics Suite
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>
            Comprehensive platform management tools for analytics, vendor accountability, and quality assurance
          </p>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
          gap: '2rem',
          marginBottom: '3rem'
        }}>
          {tools.map((tool, index) => (
            <div key={index} style={{ 
              backgroundColor: '#2d3748', 
              padding: '2rem', 
              borderRadius: '0.75rem', 
              boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
              border: '1px solid #e2e8f0',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 15px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.05)';
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: '2rem' }}>{tool.icon}</span>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a202c', margin: '0 0 0.25rem 0' }}>
                    {tool.name}
                  </h3>
                  <span style={{ 
                    fontSize: '0.75rem', 
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#10b98120',
                    color: '#10b981',
                    borderRadius: '0.25rem',
                    fontWeight: '600'
                  }}>
                    {tool.status}
                  </span>
                </div>
              </div>
              
              <p style={{ color: '#6b7280', fontSize: '0.875rem', lineHeight: '1.5', marginBottom: '1.5rem' }}>
                {tool.description}
              </p>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                  Key Features:
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {tool.features.map((feature, idx) => (
                    <span key={idx} style={{ 
                      fontSize: '0.75rem', 
                      padding: '0.25rem 0.5rem', 
                      backgroundColor: '#3b82f620',
                      color: '#3b82f6',
                      borderRadius: '0.25rem'
                    }}>
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
              
              <Link 
                to={tool.link}
                style={{
                  display: 'inline-block',
                  width: '100%',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  textAlign: 'center',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3b82f6';
                }}
              >
                Access {tool.name}
              </Link>
            </div>
          ))}
        </div>

        <div style={{ 
          backgroundColor: '#2d3748', 
          padding: '2rem', 
          borderRadius: '0.75rem', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
          border: '1px solid #e2e8f0'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1a202c', marginBottom: '1rem' }}>
            🎯 Platform Impact Summary
          </h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1.5rem' 
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>$67,890</div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Revenue Optimization Potential</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3b82f6' }}>98%</div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Review Integrity Score</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>89</div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Trusted Testers Available</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#8b5cf6' }}>156</div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Active Vendors Monitored</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}